import { Link, router, usePage, useForm } from "@inertiajs/react";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {
    BookOpenIcon,
    UserIcon,
    DocumentTextIcon,
} from "@heroicons/react/20/solid";
import CourseCard from "@/Components/Course/CourseCard";
import DocumentCard from "@/Components/Document/DocumentCard";
import { includes, toString, capitalize } from "lodash";
import { Button } from "@/shadcn/ui/button";
import { ExitIcon, ResetIcon } from "@radix-ui/react-icons";
import {
    ArrowRightToLine,
    X,
    BookOpen,
    GraduationCap,
    Loader2,
} from "lucide-react";
import { trackSearch } from "@/helpers/analytics";

interface Stage {
    id: number;
    name: string;
    group?: string;
    thumb?: string | null;
    large?: string | null;
}

interface Category {
    id: number;
    name: string;
    group: string;
    thumb?: string | null;
    large?: string | null;
}

interface Grade {
    id: number;
    name: string;
    stage_id: number;
    thumb?: string | null;
    large?: string | null;
}

interface Subject {
    id: number;
    name: string;
    grade_id: number;
    thumb?: string | null;
    large?: string | null;
}

interface Course {
    id: number;
    name: string;
    description?: string;
    image?: string;
    teacher?: {
        name: string;
    };
    subject?: {
        name: string;
        grade?: {
            name: string;
            stage?: {
                name: string;
            };
        };
    };
}

interface Document {
    id: number;
    name: string;
    description?: string;
    file?: string;
    subject?: {
        name: string;
        grade?: {
            name: string;
            stage?: {
                name: string;
            };
        };
    };
}

interface SearchDropdownProps {
    type: "course" | "document";
    queryParams?: Record<string, any>;
    placeholder?: string;
    home?: boolean;
}

function SearchDropdown({
    type,
    queryParams = {},
    placeholder,
    home = false,
}: SearchDropdownProps) {
    const { t } = useTranslation();
    const { currentLang }: any = usePage().props;
    const [searchQuery, setSearchQuery] = useState("");
    const [results, setResults] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [actualResultType, setActualResultType] = useState<"course" | "document">(type);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Debounced search
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (searchQuery.trim().length < 3) {
            setResults([]);
            setIsOpen(false);
            setError(null);
            return;
        }

        debounceTimerRef.current = setTimeout(() => {
            fetchResults();
        }, 500);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [searchQuery, queryParams]);

    const fetchResults = async () => {
        setIsLoading(true);
        setError(null);
        setActualResultType(type); // Reset to initial type when starting a new search

        try {
            const params = new URLSearchParams();
            params.append("search", searchQuery.trim());

            Object.entries(queryParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    params.append(key, value.toString());
                }
            });

            params.append("page", "1");

            const endpoint =
                type === "course" ? "/api/course" : "/api/document";
            const response = await fetch(`${endpoint}?${params.toString()}`, {
                headers: {
                    "x-api-key": "83487382947328974",
                    "Accept-Language": currentLang,
                    Accept: "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch ${type}s`);
            }

            const data = await response.json();
            let resultsData = data.data || [];
            let resultType: "course" | "document" = type;

            // If home is true, type is course, and course results are empty, try fetching documents
            if (home && type === "course" && resultsData.length === 0) {
                const documentResponse = await fetch(
                    `/api/document?${params.toString()}`,
                    {
                        headers: {
                            "x-api-key": "83487382947328974",
                            "Accept-Language": currentLang,
                            Accept: "application/json",
                        },
                    },
                );

                if (documentResponse.ok) {
                    const documentData = await documentResponse.json();
                    resultsData = documentData.data || [];
                    resultType = "document"; // Update result type to document
                }
            }

            setResults(resultsData);
            setActualResultType(resultType);
            setIsOpen(true);

            // Track search event with Google Analytics
            trackSearch(searchQuery.trim(), type, resultsData.length);
        } catch (err) {
            console.error(`Error fetching ${type}s:`, err);
            setError(
                t(
                    type === "course"
                        ? "error_fetching_courses"
                        : "error_fetching_documents",
                ),
            );
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = () => {
        setSearchQuery("");
        setResults([]);
        setIsOpen(false);
        setError(null);
        setActualResultType(type);
    };

    const handleSelect = () => {
        setIsOpen(false);
    };

    return (
        <div ref={dropdownRef} className="relative w-full">
            {/* Search Input */}
            <div className="relative">
                <div className="absolute inset-y-0 ltr:left-3 rtl:right-3 flex items-center pointer-events-none">
                    {isLoading ? (
                        <Loader2 className="h-5 w-5 text-teal-600 animate-spin" />
                    ) : (
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    )}
                </div>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={
                        placeholder ||
                        t(
                            type === "course"
                                ? "search_courses"
                                : "search_documents",
                        )
                    }
                    className="block w-full rounded-xl border border-gray-200 bg-gray-50 py-3 ltr:pl-10 rtl:pr-10 ltr:pr-12 rtl:pl-12 text-gray-900 placeholder:text-gray-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500 focus:ring-offset-0 transition-colors sm:text-sm"
                />
                {searchQuery && (
                    <button
                        onClick={handleClear}
                        className="absolute inset-y-0 ltr:right-3 rtl:left-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                )}
            </div>

            {/* Dropdown */}
            {isOpen && searchQuery.length >= 3 && (
                <div className="absolute z-50 mt-2 w-full rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 max-h-[400px] overflow-y-auto">
                    {error ? (
                        <div className="p-4 text-center text-red-600 text-sm">
                            {error}
                        </div>
                    ) : results.length > 0 ? (
                        <ul className="py-2">
                            {results.map((item) => {
                                // Determine the correct route based on home prop and actual result type
                                const itemHref = home
                                    ? actualResultType === "document"
                                        ? route(`${currentLang}.frontend.document.show`, item.id)
                                        : route(`${currentLang}.frontend.course.show`, item.id)
                                    : `/${currentLang}/${actualResultType}/${item.id}`;

                                return (
                                <li key={item.id}>
                                    <Link
                                        href={itemHref}
                                        onClick={handleSelect}
                                        className="flex items-start gap-3 px-4 py-3 hover:bg-teal-50 transition-colors cursor-pointer group"
                                    >
                                        {/* Icon/Image */}
                                        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center group-hover:from-teal-200 group-hover:to-teal-300 transition-all">
                                            {item.thumb ? (
                                                <img
                                                    src={item.thumb}
                                                    alt={item.name}
                                                    className="w-full h-full object-cover rounded-lg"
                                                />
                                            ) : type === "course" ? (
                                                <BookOpenIcon className="h-6 w-6 text-teal-600" />
                                            ) : (
                                                <DocumentTextIcon className="h-6 w-6 text-teal-600" />
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            {/* Name */}
                                            <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-700 transition-colors line-clamp-1 ltr:text-left rtl:text-right">
                                                {item.name}
                                            </p>

                                            {/* Description */}
                                            {item.description && (
                                                <p className="text-xs text-gray-600 line-clamp-1 mt-0.5 ltr:text-left rtl:text-right">
                                                    {item.description}
                                                </p>
                                            )}

                                            {/* Meta Info */}
                                            <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                                {/* Teacher (only for courses) */}
                                                {type === "course" &&
                                                    item.teacher && (
                                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                                            <UserIcon className="h-3 w-3" />
                                                            <span className="line-clamp-1">
                                                                {
                                                                    item.teacher
                                                                        .name
                                                                }
                                                            </span>
                                                        </div>
                                                    )}
                                                {/* Subject */}
                                                {item.subject && (
                                                    <div className="flex items-center gap-1 text-xs text-gray-500">
                                                        <span className="line-clamp-1">
                                                            {item.subject.name}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Price */}
                                        {item.sale_price !== undefined &&
                                            item.sale_price > 0 && (
                                                <div className="flex-shrink-0 text-right">
                                                    <p className="text-sm font-bold text-teal-600">
                                                        {item.sale_price}{" "}
                                                        {t("kd")}
                                                    </p>
                                                </div>
                                            )}
                                    </Link>
                                </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div className="p-8 text-center">
                            <div className="flex flex-col items-center gap-3">
                                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                                    <MagnifyingGlassIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        {t(
                                            type === "course"
                                                ? "no_courses_found"
                                                : "no_documents_found",
                                        )}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t("try_different_keywords")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Hint Text */}
            {searchQuery.length > 0 && searchQuery.length < 3 && (
                <p className="mt-1.5 text-xs text-gray-500 ltr:text-left rtl:text-right">
                    {t("type_at_least_3_characters")}
                </p>
            )}
        </div>
    );
}

interface SearchResultsProps {
    searchParams: {
        stage_id?: number;
        university_id?: number;
        college_id?: number;
        grade_id?: number;
        subject_id?: number;
    };
}

function SearchResultsComponent({ searchParams }: SearchResultsProps) {
    const { currentLang, currentRouteName } = usePage().props;
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<"courses" | "documents">(
        "courses",
    );
    const [courses, setCourses] = useState<Course[]>([]);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [loadingDocuments, setLoadingDocuments] = useState(false);

    const fetchCourses = async () => {
        setLoadingCourses(true);
        try {
            const queryParams = new URLSearchParams();
            Object.entries(searchParams).forEach(([key, value]) => {
                if (value) queryParams.append(key, value.toString());
            });

            const response = await fetch(
                `/api/course?${queryParams.toString()}`,
                {
                    headers: {
                        "x-api-key": "83487382947328974",
                        "Accept-Language": `${currentLang}`,
                    },
                },
            );

            const data = await response.json();
            setCourses(data.data?.slice(0, 3) || []);
        } catch (error) {
            console.error("Error fetching courses:", error);
            setCourses([]);
        } finally {
            setLoadingCourses(false);
        }
    };

    const fetchDocuments = async () => {
        setLoadingDocuments(true);
        try {
            const queryParams = new URLSearchParams();
            Object.entries(searchParams).forEach(([key, value]) => {
                if (value) queryParams.append(key, value.toString());
            });

            const response = await fetch(
                `/api/document?${queryParams.toString()}`,
                {
                    headers: {
                        "x-api-key": "83487382947328974",
                        "Accept-Language": `${currentLang}`,
                    },
                },
            );
            const data = await response.json();
            setDocuments(data.data?.slice(0, 3) || []);
        } catch (error) {
            console.error("Error fetching documents:", error);
            setDocuments([]);
        } finally {
            setLoadingDocuments(false);
        }
    };

    useEffect(() => {
        if (Object.values(searchParams).some((value) => value)) {
            fetchCourses();
            fetchDocuments();
        }
    }, [searchParams]);

    const EmptyState = ({ type }: { type: "courses" | "documents" }) => (
        <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <ExclamationTriangleIcon className="w-16 h-16 mb-4 text-gray-400" />
            <h3 className="text-lg font-medium mb-2">
                {type == "courses"
                    ? t("no_courses_found")
                    : t("no_documents_found")}
            </h3>
            <p className="text-sm text-center">
                {t("try_adjusting_your_search_criteria")}
            </p>
        </div>
    );

    return (
        <div className="mt-6 bg-gray-50 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-center mb-4">
                {t("search_results")}
            </h3>

            {/* Tabs */}
            <div className="flex justify-center mb-4">
                <div className="bg-white rounded-3xl p-2 flex w-3/4 border border-gray-100 justify-center items-center">
                    <button
                        onClick={() => setActiveTab("courses")}
                        className={`px-4 py-4 rounded-3xl text-md font-medium transition-colors w-1/2 flex items-center justify-center gap-2 ${
                            activeTab == "courses"
                                ? "bg-teal-600 text-white"
                                : "text-gray-600 hover:text-gray-900"
                        }`}
                    >
                        <GraduationCap className="size-5 rtl:order-2" />
                        <span className="rtl:order-1">
                            {t("courses")} ({courses.length})
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab("documents")}
                        className={`px-4 py-4 rounded-3xl text-md font-medium transition-colors w-1/2 flex items-center justify-center gap-2 ${
                            activeTab == "documents"
                                ? "bg-teal-600 text-white"
                                : "text-gray-600 hover:text-gray-900"
                        }`}
                    >
                        <BookOpen className="size-5 rtl:order-2" />
                        <span className="rtl:order-1">
                            {t("documents")} ({documents.length})
                        </span>
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="space-y-3">
                {activeTab == "courses" && (
                    <>
                        {loadingCourses ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                            </div>
                        ) : courses.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {courses.map((course) => (
                                    <CourseCard
                                        key={course.id}
                                        element={course}
                                    />
                                ))}
                            </div>
                        ) : (
                            <EmptyState type="courses" />
                        )}
                    </>
                )}

                {activeTab == "documents" && (
                    <>
                        {loadingDocuments ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : documents.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {documents.map((document) => (
                                    <DocumentCard
                                        key={document.id}
                                        element={document}
                                    />
                                ))}
                            </div>
                        ) : (
                            <EmptyState type="documents" />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

interface CourseExplorerProps {
    type?: "course" | "document";
    stages: Stage[];
    categories: Category[];
    grades: Grade[];
    subjects: Subject[];
    home?: boolean;
    onExplore?: (params: {
        stage_id?: number;
        university_id?: number;
        college_id?: number;
        grade_id?: number;
        subject_id?: number;
        tab: "courses" | "documents";
    }) => void;
}

export default function CourseExplorer({
    type = "course",
    stages,
    categories,
    grades,
    subjects,
    onExplore,
    home = false,
}: CourseExplorerProps) {
    const { t } = useTranslation();

    // Helper function to validate if image URL contains valid image extensions
    const isValidImageUrl = (url?: string | null): boolean => {
        if (!url) return false;
        const validExtensions = [".jpg", ".jpeg", ".png"];
        const lowerUrl = url.toLowerCase();
        return validExtensions.some((ext) => lowerUrl.includes(ext));
    };
    const {
        currentLang,
        ziggy: { query },
        currentRouteName,
    }: any = usePage().props;
    const current = route().current()?.toString();
    const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
    const [selectedUniversity, setSelectedUniversity] =
        useState<Category | null>(null);
    const [selectedCollege, setSelectedCollege] = useState<Category | null>(
        null,
    );
    const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
    const [selectedSubject, setSelectedSubject] = useState<Subject | null>(
        null,
    );
    const [currentStep, setCurrentStep] = useState<number>(1);

    const isUniversityStage = selectedStage?.group == "university";

    const filteredUniversities = categories.filter(
        (cat) => cat.group == "university",
    );

    const filteredColleges = categories.filter((cat) => cat.group == "college");

    const filteredGrades = grades.filter(
        (grade) => grade.stage_id == selectedStage?.id,
    );

    const filteredSubjects = subjects.filter(
        (subject) => subject.grade_id == selectedGrade?.id,
    );

    // Initialize state from URL parameters
    useEffect(() => {
        if (query.stage_id) {
            const stage = stages.find((s) => s.id == query.stage_id);
            if (stage) {
                setSelectedStage(stage);
                const isUniversity = stage.group == "university";
                if (query.university_id) {
                    const university = categories.find(
                        (c) =>
                            c.id == query.university_id &&
                            c.group == "university",
                    );
                    if (university) {
                        setSelectedUniversity(university);
                        if (query.college_id) {
                            const college = categories.find(
                                (c) =>
                                    c.id == query.college_id &&
                                    c.group == "college",
                            );
                            if (college) {
                                setSelectedCollege(college);
                                if (query.grade_id) {
                                    const grade = grades.find(
                                        (g) =>
                                            g.id == query.grade_id &&
                                            g.stage_id == stage.id,
                                    );
                                    if (grade) {
                                        setSelectedGrade(grade);
                                        if (query.subject_id) {
                                            const subject = subjects.find(
                                                (s) =>
                                                    s.id == query.subject_id &&
                                                    s.grade_id == grade.id,
                                            );
                                            if (subject) {
                                                setSelectedSubject(subject);
                                                setCurrentStep(6);
                                            } else {
                                                setCurrentStep(5);
                                            }
                                        } else {
                                            setCurrentStep(5);
                                        }
                                    } else {
                                        setCurrentStep(4);
                                    }
                                } else {
                                    setCurrentStep(4);
                                }
                            } else {
                                setCurrentStep(3);
                            }
                        } else {
                            setCurrentStep(3);
                        }
                    } else if (isUniversity) {
                        setCurrentStep(2);
                    }
                } else if (isUniversity) {
                    setCurrentStep(2);
                } else if (query.grade_id) {
                    const grade = grades.find(
                        (g) => g.id == query.grade_id && g.stage_id == stage.id,
                    );
                    if (grade) {
                        setSelectedGrade(grade);
                        if (query.subject_id) {
                            const subject = subjects.find(
                                (s) =>
                                    s.id == query.subject_id &&
                                    s.grade_id == grade.id,
                            );
                            if (subject) {
                                setSelectedSubject(subject);
                                setCurrentStep(4);
                            } else {
                                setCurrentStep(3);
                            }
                        } else {
                            setCurrentStep(3);
                        }
                    } else {
                        setCurrentStep(2);
                    }
                } else {
                    setCurrentStep(2);
                }
            }
        }
    }, [query, stages, categories, grades, subjects]);

    const handleStageSelect = (stage: Stage) => {
        setSelectedStage(stage);
        setSelectedUniversity(null);
        setSelectedCollege(null);
        setSelectedGrade(null);
        setSelectedSubject(null);

        const nextStep = 2;
        setCurrentStep(nextStep);

        // Update URL with stage_id parameter
        const params: any = { ...query, stage_id: stage.id };
        // Remove other parameters when stage changes
        delete params.university_id;
        delete params.college_id;
        delete params.grade_id;
        delete params.subject_id;

        // router.get(route(`${currentLang}.frontend.home`), params, {
        //     preserveScroll: true,
        //     preserveState: true,
        //     replace: true,
        // });
        router.get(route(currentRouteName), params, {
            preserveScroll: true,
            preserveState: true,
            replace: true,
        });
    };

    const handleUniversitySelect = (university: Category) => {
        setSelectedUniversity(university);
        setSelectedCollege(null);
        setSelectedGrade(null);
        setSelectedSubject(null);
        setCurrentStep(3);

        // Update URL with university_id parameter
        const params: any = { ...query };
        if (selectedStage) params.stage_id = selectedStage.id;
        params.university_id = university.id;
        // Remove subsequent parameters
        delete params.college_id;
        delete params.grade_id;
        delete params.subject_id;

        // router.get(route(`${currentLang}.frontend.home`), params, {
        //     preserveScroll: true,
        //     preserveState: true,
        //     replace: true,
        // });

        router.get(route(currentRouteName), params, {
            preserveScroll: true,
            preserveState: true,
            replace: true,
        });
    };

    const handleCollegeSelect = (college: Category) => {
        setSelectedCollege(college);
        setSelectedGrade(null);
        setSelectedSubject(null);
        setCurrentStep(4);

        // Update URL with college_id parameter
        const params: any = { ...query };
        if (selectedStage) params.stage_id = selectedStage.id;
        if (selectedUniversity) params.university_id = selectedUniversity.id;
        params.college_id = college.id;
        // Remove subsequent parameters
        delete params.grade_id;
        delete params.subject_id;

        router.get(route(currentRouteName), params, {
            preserveScroll: true,
            preserveState: true,
            replace: true,
        });
        // router.get(route(`${currentLang}.frontend.home`), params, {
        //     preserveScroll: true,
        //     preserveState: true,
        //     replace: true,
        // });
    };

    const handleGradeSelect = (grade: Grade) => {
        setSelectedGrade(grade);
        setSelectedSubject(null);
        setCurrentStep(isUniversityStage ? 5 : 3);

        // Update URL with grade_id parameter
        const params: any = { ...query };
        if (selectedStage) params.stage_id = selectedStage.id;
        if (selectedUniversity) params.university_id = selectedUniversity.id;
        if (selectedCollege) params.college_id = selectedCollege.id;
        params.grade_id = grade.id;
        // Remove subsequent parameters
        delete params.subject_id;
        router.get(route(currentRouteName), params, {
            preserveScroll: true,
            preserveState: true,
            replace: true,
        });
        // router.get(route(`${currentLang}.frontend.home`), params, {
        //     preserveScroll: true,
        //     preserveState: true,
        //     replace: true,
        // });
    };

    const handleSubjectSelect = (subject: Subject) => {
        setSelectedSubject(subject);
        setCurrentStep(isUniversityStage ? 6 : 4);

        // Update URL with subject_id parameter
        const params: any = { ...query };
        if (selectedStage) params.stage_id = selectedStage.id;
        if (selectedUniversity) params.university_id = selectedUniversity.id;
        if (selectedCollege) params.college_id = selectedCollege.id;
        if (selectedGrade) params.grade_id = selectedGrade.id;
        params.subject_id = subject.id;
        router.get(route(currentRouteName), params, {
            preserveScroll: true,
            preserveState: true,
            replace: true,
        });
        // router.get(route(`${currentLang}.frontend.home`), params, {
        //     preserveScroll: true,
        //     preserveState: true,
        //     replace: true,
        // });
    };

    const handleTabClick = (tab: "courses" | "documents") => {
        const params: any = { ...query };
        if (selectedStage) params.stage_id = selectedStage.id;
        if (selectedUniversity) params.university_id = selectedUniversity.id;
        if (selectedCollege) params.college_id = selectedCollege.id;
        if (selectedGrade) params.grade_id = selectedGrade.id;
        if (selectedSubject) params.subject_id = selectedSubject.id;

        if (onExplore) {
            onExplore({ ...params, tab });
        } else {
            const routeName =
                tab == "courses"
                    ? `${currentLang}.frontend.course.index`
                    : `${currentLang}.frontend.document.index`;

            router.get(route(routeName, params));
        }
    };

    const getStepTitle = (step: number) => {
        if (isUniversityStage) {
            const titles = [
                t("educational_stage"),
                t("universities"),
                t("colleges"),
                t("academic_year"),
                t("educational_materials"),
                t("courses_and_notes"),
            ];
            return titles[step - 1];
        } else {
            const titles = [
                t("educational_stage"),
                t("academic_year"),
                t("educational_materials"),
                t("courses_and_notes"),
            ];
            return titles[step - 1];
        }
    };

    const handleClickBack = () => {
        if (currentStep > 1) {
            const newStep = currentStep - 1;
            setCurrentStep(newStep);

            // Prepare params for URL update
            const params: any = { ...query };

            if (currentStep == 2) {
                // Going back to step 1, remove all parameters
                setSelectedStage(null);
                delete params.stage_id;
                delete params.university_id;
                delete params.college_id;
                delete params.grade_id;
                delete params.subject_id;
            } else if (currentStep == 3) {
                if (isUniversityStage) {
                    // Going back to step 2 (universities), remove university and subsequent params
                    setSelectedUniversity(null);
                    delete params.university_id;
                    delete params.college_id;
                    delete params.grade_id;
                    delete params.subject_id;
                } else {
                    // Going back to step 2 (grades), remove grade and subsequent params
                    setSelectedGrade(null);
                    delete params.grade_id;
                    delete params.subject_id;
                }
            } else if (currentStep == 4) {
                if (isUniversityStage) {
                    // Going back to step 3 (colleges), remove college and subsequent params
                    setSelectedCollege(null);
                    delete params.college_id;
                    delete params.grade_id;
                    delete params.subject_id;
                } else {
                    // Going back to step 3 (subjects), remove subject param
                    setSelectedSubject(null);
                    delete params.subject_id;
                }
            } else if (currentStep == 5) {
                if (isUniversityStage) {
                    // Going back to step 4 (grades), remove grade and subsequent params
                    setSelectedGrade(null);
                    delete params.grade_id;
                    delete params.subject_id;
                }
            } else if (currentStep == 6) {
                if (isUniversityStage) {
                    // Going back to step 5 (subjects), remove subject param
                    setSelectedSubject(null);
                    delete params.subject_id;
                }
            }

            // Update URL with cleaned parameters
            router.get(route(currentRouteName), params, {
                preserveScroll: true,
                preserveState: true,
                replace: true,
            });
            // router.get(
            //     route(`${currentLang}.frontend.home`),
            //     params,
            //     {
            //         preserveScroll: true,
            //         preserveState: true,
            //         replace: true,
            //     },
            // );
        }
    };

    return (
        <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl">
            {/* Explore */}
            <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                    {t(
                        home
                            ? "explore_available_courses_and_documents"
                            : type === "course"
                              ? "explore_available_courses"
                              : "explore_available_documents",
                    )}
                </h2>
                <p className="text-sm sm:text-base text-gray-600 px-2">
                    {t(
                        type === "course"
                            ? "choose_educational_stage_to_access_course"
                            : "choose_educational_stage_to_access_document",
                    )}
                </p>

                {/* Search Input */}
                <div className="mx-auto mt-4 max-w-md">
                    <SearchDropdown
                        type={type}
                        queryParams={{
                            stage_id: selectedStage?.id,
                            university_id: selectedUniversity?.id,
                            college_id: selectedCollege?.id,
                            grade_id: selectedGrade?.id,
                            subject_id: selectedSubject?.id,
                        }}
                        placeholder={capitalize(
                            t(
                                type === "course"
                                    ? "search_courses_teachers_subjects"
                                    : "search_documents",
                            ),
                        )}
                        home={home}
                    />
                </div>
            </div>

            {/* Progress Steps */}
            <div className="mb-6 sm:mb-10 px-2 sm:px-4">
                <div className="max-w-6xl mx-auto">
                    {/* Desktop View */}
                    <div className="hidden sm:flex items-center justify-center">
                        <div className="flex items-center space-x-3 lg:space-x-6">
                            {Array.from(
                                { length: isUniversityStage ? 6 : 4 },
                                (_, index) => {
                                    const step = index + 1;
                                    const isActive = currentStep >= step;
                                    const isCurrent = currentStep == step;
                                    const isCompleted = currentStep > step;
                                    const totalSteps = isUniversityStage
                                        ? 6
                                        : 4;

                                    return (
                                        <div
                                            key={step}
                                            className="flex items-center"
                                        >
                                            {/* Step Circle and Title */}
                                            <div className="flex flex-col items-center min-w-[90px] lg:min-w-[110px]">
                                                {/* Step Circle */}
                                                <div className="relative mb-3">
                                                    <div
                                                        className={`w-14 h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center font-bold transition-all duration-500 relative z-10 ${
                                                            isCompleted
                                                                ? "bg-gradient-to-br from-teal-500 to-teal-700 text-white shadow-xl transform scale-105"
                                                                : isCurrent
                                                                  ? "bg-gradient-to-br from-teal-600 to-teal-800 text-white shadow-lg transform scale-110"
                                                                  : isActive
                                                                    ? "bg-teal-600 text-white shadow-md"
                                                                    : "bg-gray-300 border border-gray-200 text-white"
                                                        }`}
                                                    >
                                                        {isCompleted ? (
                                                            <svg
                                                                className="w-7 h-7 lg:w-8 lg:h-8"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        ) : (
                                                            <span className="text-lg lg:text-xl font-extrabold">
                                                                {step}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Pulse Effect for Current Step */}
                                                    {isCurrent && (
                                                        <div className="absolute inset-0 rounded-full bg-teal-600 animate-ping opacity-20"></div>
                                                    )}

                                                    {/* Glow Effect for Active Steps */}
                                                    {(isActive ||
                                                        isCompleted) && (
                                                        <div className="absolute inset-0 rounded-full bg-teal-500 opacity-20 blur-sm"></div>
                                                    )}
                                                </div>

                                                {/* Step Title */}
                                                <div className="text-center">
                                                    <span
                                                        className={`text-sm lg:text-base font-semibold leading-tight transition-colors duration-300 ${
                                                            isCurrent
                                                                ? "text-teal-700"
                                                                : isActive
                                                                  ? "text-teal-600"
                                                                  : "text-gray-500"
                                                        }`}
                                                    >
                                                        {getStepTitle(step)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Arrow Connector */}
                                            {step < totalSteps && (
                                                <div className="flex items-center justify-center mx-1 -mt-12">
                                                    {currentLang == "ar" ? (
                                                        <ChevronLeftIcon
                                                            className={`size-5 transition-colors duration-300 ${
                                                                currentStep >
                                                                step
                                                                    ? "text-teal-600"
                                                                    : "text-gray-400"
                                                            }`}
                                                        />
                                                    ) : (
                                                        <ChevronRightIcon
                                                            className={`size-5 transition-colors duration-300 ${
                                                                currentStep >
                                                                step
                                                                    ? "text-teal-600"
                                                                    : "text-gray-400"
                                                            }`}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                },
                            )}
                        </div>
                    </div>

                    {/* Mobile View */}
                    <div className="sm:hidden">
                        <div className="flex flex-wrap justify-center gap-y-4 gap-x-1">
                            {Array.from(
                                { length: isUniversityStage ? 6 : 4 },
                                (_, index) => {
                                    const step = index + 1;
                                    const isActive = currentStep >= step;
                                    const isCurrent = currentStep == step;
                                    const isCompleted = currentStep > step;
                                    const totalSteps = isUniversityStage
                                        ? 6
                                        : 4;

                                    return (
                                        <div
                                            key={step}
                                            className="flex items-center"
                                        >
                                            {/* Step Circle and Title */}
                                            <div className="flex flex-col items-center min-w-[70px]">
                                                {/* Step Circle */}
                                                <div className="relative mb-2">
                                                    <div
                                                        className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all duration-500 relative z-10 ${
                                                            isCompleted
                                                                ? "bg-gradient-to-br from-teal-500 to-teal-700 text-white shadow-xl transform scale-105"
                                                                : isCurrent
                                                                  ? "bg-gradient-to-br from-teal-600 to-teal-800 text-white shadow-lg transform scale-110"
                                                                  : isActive
                                                                    ? "bg-teal-600 text-white shadow-md"
                                                                    : "bg-gray-100 border-2 border-gray-300 text-gray-400"
                                                        }`}
                                                    >
                                                        {isCompleted ? (
                                                            <svg
                                                                className="w-6 h-6"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        ) : (
                                                            <span className="text-base font-extrabold">
                                                                {step}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Pulse Effect for Current Step */}
                                                    {isCurrent && (
                                                        <div className="absolute inset-0 rounded-full bg-teal-600 animate-ping opacity-20"></div>
                                                    )}

                                                    {/* Glow Effect for Active Steps */}
                                                    {(isActive ||
                                                        isCompleted) && (
                                                        <div className="absolute inset-0 rounded-full bg-teal-500 opacity-20 blur-sm"></div>
                                                    )}
                                                </div>

                                                {/* Step Title */}
                                                <div className="text-center">
                                                    <span
                                                        className={`text-xs font-semibold leading-tight transition-colors duration-300 block ${
                                                            isCurrent
                                                                ? "text-teal-700"
                                                                : isActive
                                                                  ? "text-teal-600"
                                                                  : "text-gray-500"
                                                        }`}
                                                    >
                                                        {getStepTitle(step)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Arrow Connector for Mobile - Only show between some steps */}
                                            {step < totalSteps &&
                                                (step % 3 !== 0 ||
                                                    totalSteps <= 4) && (
                                                    <div className="flex items-center justify-center mx-1 -mt-8">
                                                        {currentLang == "ar" ? (
                                                            <ChevronLeftIcon
                                                                className={`w-4 h-4 transition-colors duration-300 ${
                                                                    currentStep >
                                                                    step
                                                                        ? "text-teal-600"
                                                                        : "text-gray-400"
                                                                }`}
                                                            />
                                                        ) : (
                                                            <ChevronRightIcon
                                                                className={`w-4 h-4 transition-colors duration-300 ${
                                                                    currentStep >
                                                                    step
                                                                        ? "text-teal-600"
                                                                        : "text-gray-400"
                                                                }`}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                    );
                                },
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Step 1: Educational Stage */}
            {currentStep == 1 && (
                <div className="space-y-4">
                    <div className="flex flex-row justify-between items-center">
                        <Button
                            onClick={handleClickBack}
                            variant="ghost"
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <ChevronLeftIcon className="rtl:rotate-180 size-6 text-teal-600 hover:text-white" />
                        </Button>
                        <h3 className="text-base sm:text-lg font-semibold text-center ">
                            {t("educational_stage")}
                        </h3>
                        <div className="col-span-1"></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4 ">
                        {stages.map((stage) => {
                            // Only include stage_id, remove all other parameters
                            const params: any = {
                                stage_id: stage.id,
                            };

                            const content = (
                                <>
                                    <div className="size-14 sm:size-24 p-2 bg-teal-50 rounded-full flex items-center justify-center overflow-hidden">
                                        {stage.thumb &&
                                        isValidImageUrl(stage.thumb) ? (
                                            <img
                                                src={stage.large || stage.thumb}
                                                alt={stage.name}
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <span className="text-xl sm:text-2xl">
                                                🎓
                                            </span>
                                        )}
                                    </div>
                                    <span className="font-medium text-center text-sm sm:text-base">
                                        {stage.name}
                                    </span>
                                </>
                            );

                            return home ? (
                                <button
                                    key={stage.id}
                                    onClick={() => handleStageSelect(stage)}
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </button>
                            ) : (
                                <Link
                                    key={stage.id}
                                    href={route(
                                        type === "course"
                                            ? `${currentLang}.frontend.course.index`
                                            : `${currentLang}.frontend.document.index`,
                                        params,
                                    )}
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Step 2: Universities (University Stage) or Grades (Other Stages) */}
            {currentStep == 2 && isUniversityStage && (
                <div className="space-y-4">
                    <div className="flex flex-row justify-between items-center">
                        <Button
                            onClick={handleClickBack}
                            variant="ghost"
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <ChevronLeftIcon className="rtl:rotate-180 size-6 text-teal-600 hover:text-white" />
                        </Button>
                        <h3 className="text-base sm:text-lg font-semibold text-center ">
                            {t("universities")}
                        </h3>
                        <Button
                            variant="ghost"
                            asChild
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <Link
                                href={`${route(current || "home")}`}
                                preserveScroll
                            >
                                <ArrowRightToLine className="ltr:rotate-180 size-6 text-teal-600 hover:text-white" />
                            </Link>
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {filteredUniversities.map((university) => {
                            const params: any = {};
                            if (selectedStage)
                                params.stage_id = selectedStage.id;
                            params.university_id = university.id;

                            const content = (
                                <>
                                    <div className="size-14 sm:size-24 p-2 bg-teal-50 rounded-full flex items-center justify-center overflow-hidden">
                                        {university.thumb &&
                                        isValidImageUrl(university.thumb) ? (
                                            <img
                                                src={
                                                    university.large ||
                                                    university.thumb
                                                }
                                                alt={university.name}
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <span className="text-xl sm:text-2xl">
                                                🏛️
                                            </span>
                                        )}
                                    </div>
                                    <span className="font-medium text-center text-sm sm:text-base">
                                        {university.name}
                                    </span>
                                </>
                            );

                            return home ? (
                                <button
                                    key={university.id}
                                    onClick={() =>
                                        handleUniversitySelect(university)
                                    }
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </button>
                            ) : (
                                <Link
                                    key={university.id}
                                    href={route(
                                        type === "course"
                                            ? `${currentLang}.frontend.course.index`
                                            : `${currentLang}.frontend.document.index`,
                                        params,
                                    )}
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {currentStep == 2 && !isUniversityStage && (
                <div className="space-y-4">
                    <div className="flex flex-row justify-between items-center">
                        <Button
                            onClick={handleClickBack}
                            variant="ghost"
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <ChevronLeftIcon className="rtl:rotate-180 size-6 text-teal-600 hover:text-white" />
                        </Button>
                        <h3 className="text-base sm:text-lg font-semibold text-center ">
                            {t("academic_year")}
                        </h3>
                        <Button
                            variant="ghost"
                            asChild
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <Link
                                href={`${route(current || "home")}`}
                                preserveScroll
                            >
                                <ArrowRightToLine className="ltr:rotate-180 size-6 text-teal-600 hover:text-white" />
                            </Link>
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {filteredGrades.map((grade) => {
                            const params: any = {};
                            if (selectedStage)
                                params.stage_id = selectedStage.id;
                            params.grade_id = grade.id;

                            const content =
                                grade.thumb && isValidImageUrl(grade.thumb) ? (
                                    <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                                        <div className="size-14 sm:size-24 p-2 bg-teal-50 rounded-full flex items-center justify-center overflow-hidden">
                                            <img
                                                src={grade.large || grade.thumb}
                                                alt={grade.name}
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <span className="font-medium text-center text-sm sm:text-base">
                                            {grade.name}
                                        </span>
                                    </div>
                                ) : (
                                    <span className="font-medium text-center text-sm sm:text-base">
                                        {grade.name}
                                    </span>
                                );

                            return home ? (
                                <button
                                    key={grade.id}
                                    onClick={() => handleGradeSelect(grade)}
                                    className="p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex items-center justify-center"
                                >
                                    {content}
                                </button>
                            ) : (
                                <Link
                                    key={grade.id}
                                    href={route(
                                        type === "course"
                                            ? `${currentLang}.frontend.course.index`
                                            : `${currentLang}.frontend.document.index`,
                                        params,
                                    )}
                                    className="p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex items-center justify-center"
                                >
                                    {content}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Step 3: Colleges (University Stage) or Subjects (Other Stages) */}
            {currentStep == 3 && isUniversityStage && (
                <div className="space-y-4">
                    <div className="flex flex-row justify-between items-center">
                        <Button
                            onClick={handleClickBack}
                            variant="ghost"
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <ChevronLeftIcon className="rtl:rotate-180 size-6 text-teal-600 hover:text-white" />
                        </Button>
                        <h3 className="text-base sm:text-lg font-semibold text-center ">
                            {t("colleges")}
                        </h3>
                        <Button
                            variant="ghost"
                            asChild
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <Link
                                href={`${route(current || "home")}`}
                                preserveScroll
                            >
                                <ArrowRightToLine className="ltr:rotate-180 size-6 text-teal-600 hover:text-white" />
                            </Link>
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {filteredColleges.map((college) => {
                            const params: any = {};
                            if (selectedStage)
                                params.stage_id = selectedStage.id;
                            if (selectedUniversity)
                                params.university_id = selectedUniversity.id;
                            params.college_id = college.id;

                            const content = (
                                <>
                                    <div className="size-14 sm:size-24 p-2 bg-teal-50 rounded-full flex items-center justify-center overflow-hidden">
                                        {college.thumb &&
                                        isValidImageUrl(college.thumb) ? (
                                            <img
                                                src={
                                                    college.large ||
                                                    college.thumb
                                                }
                                                alt={college.name}
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <span className="text-xl sm:text-2xl">
                                                🏫
                                            </span>
                                        )}
                                    </div>
                                    <span className="font-medium text-center text-sm sm:text-base">
                                        {college.name}
                                    </span>
                                </>
                            );

                            return home ? (
                                <button
                                    key={college.id}
                                    onClick={() => handleCollegeSelect(college)}
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </button>
                            ) : (
                                <Link
                                    key={college.id}
                                    href={route(
                                        type === "course"
                                            ? `${currentLang}.frontend.course.index`
                                            : `${currentLang}.frontend.document.index`,
                                        params,
                                    )}
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {currentStep == 3 && !isUniversityStage && (
                <div className="space-y-4">
                    <div className="flex flex-row justify-between items-center">
                        <Button
                            onClick={handleClickBack}
                            variant="ghost"
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <ChevronLeftIcon className="rtl:rotate-180 size-6 text-teal-600 hover:text-white" />
                        </Button>
                        <h3 className="text-base sm:text-lg font-semibold text-center ">
                            {t("educational_materials")}
                        </h3>
                        <Button
                            variant="ghost"
                            asChild
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <Link
                                href={`${route(current || "home")}`}
                                preserveScroll
                            >
                                <ArrowRightToLine className="ltr:rotate-180 size-6 text-teal-600 hover:text-white" />
                            </Link>
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {filteredSubjects.map((subject) => {
                            const params: any = {};
                            if (selectedStage)
                                params.stage_id = selectedStage.id;
                            if (selectedGrade)
                                params.grade_id = selectedGrade.id;
                            params.subject_id = subject.id;

                            const content = (
                                <>
                                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-teal-100 rounded-full flex items-center justify-center overflow-hidden">
                                        {subject.thumb &&
                                        isValidImageUrl(subject.thumb) ? (
                                            <img
                                                src={
                                                    subject.large ||
                                                    subject.thumb
                                                }
                                                alt={subject.name}
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <span className="text-xl sm:text-2xl">
                                                📚
                                            </span>
                                        )}
                                    </div>
                                    <span className="font-medium text-center text-sm sm:text-base">
                                        {subject.name}
                                    </span>
                                </>
                            );

                            return home ? (
                                <button
                                    key={subject.id}
                                    onClick={() => handleSubjectSelect(subject)}
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </button>
                            ) : (
                                <Link
                                    key={subject.id}
                                    href={route(
                                        type === "course"
                                            ? `${currentLang}.frontend.course.index`
                                            : `${currentLang}.frontend.document.index`,
                                        params,
                                    )}
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Step 4: Grades (University Stage) */}
            {currentStep == 4 && isUniversityStage && (
                <div className="space-y-4">
                    <div className="flex flex-row justify-between items-center">
                        <Button
                            onClick={handleClickBack}
                            variant="ghost"
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <ChevronLeftIcon className="rtl:rotate-180 size-6 text-teal-600 hover:text-white" />
                        </Button>
                        <h3 className="text-base sm:text-lg font-semibold text-center ">
                            {t("academic_year")}
                        </h3>
                        <Button
                            variant="ghost"
                            asChild
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <Link
                                href={`${route(current || "home")}`}
                                preserveScroll
                            >
                                <ArrowRightToLine className="ltr:rotate-180 size-6 text-teal-600 hover:text-white" />
                            </Link>
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {filteredGrades.map((grade) => {
                            const params: any = {};
                            if (selectedStage)
                                params.stage_id = selectedStage.id;
                            if (selectedUniversity)
                                params.university_id = selectedUniversity.id;
                            if (selectedCollege)
                                params.college_id = selectedCollege.id;
                            params.grade_id = grade.id;

                            const content =
                                grade.thumb && isValidImageUrl(grade.thumb) ? (
                                    <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                                        <div className="size-14 sm:size-24 p-2 bg-teal-50 rounded-full flex items-center justify-center overflow-hidden">
                                            <img
                                                src={grade.large || grade.thumb}
                                                alt={grade.name}
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <span className="font-medium text-center text-sm sm:text-base">
                                            {grade.name}
                                        </span>
                                    </div>
                                ) : (
                                    <span className="font-medium text-center text-sm sm:text-base">
                                        {grade.name}
                                    </span>
                                );

                            return home ? (
                                <button
                                    key={grade.id}
                                    onClick={() => handleGradeSelect(grade)}
                                    className="p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex items-center justify-center"
                                >
                                    {content}
                                </button>
                            ) : (
                                <Link
                                    key={grade.id}
                                    href={route(
                                        type === "course"
                                            ? `${currentLang}.frontend.course.index`
                                            : `${currentLang}.frontend.document.index`,
                                        params,
                                    )}
                                    className="p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex items-center justify-center"
                                >
                                    {content}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Step 5: Subjects (University Stage) or Final Tabs (Other Stages) */}
            {currentStep == 5 && isUniversityStage && (
                <div className="space-y-4">
                    <div className="flex flex-row justify-between items-center">
                        <Button
                            onClick={handleClickBack}
                            variant="ghost"
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <ChevronLeftIcon className="rtl:rotate-180 size-6 text-teal-600 hover:text-white" />
                        </Button>
                        <h3 className="text-base sm:text-lg font-semibold text-center ">
                            {t("educational_materials")}
                        </h3>
                        <Button
                            variant="ghost"
                            asChild
                            className="size-10 p-0 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200"
                        >
                            <Link
                                href={`${route(current || "home")}`}
                                preserveScroll
                            >
                                <ArrowRightToLine className="ltr:rotate-180 size-6 text-teal-600 hover:text-white" />
                            </Link>
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {filteredSubjects.map((subject) => {
                            const params: any = {};
                            if (selectedStage)
                                params.stage_id = selectedStage.id;
                            if (selectedUniversity)
                                params.university_id = selectedUniversity.id;
                            if (selectedCollege)
                                params.college_id = selectedCollege.id;
                            if (selectedGrade)
                                params.grade_id = selectedGrade.id;
                            params.subject_id = subject.id;

                            const content = (
                                <>
                                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-teal-100 rounded-full flex items-center justify-center overflow-hidden">
                                        {subject.thumb &&
                                        isValidImageUrl(subject.thumb) ? (
                                            <img
                                                src={
                                                    subject.large ||
                                                    subject.thumb
                                                }
                                                alt={subject.name}
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <span className="text-xl sm:text-2xl">
                                                📚
                                            </span>
                                        )}
                                    </div>
                                    <span className="font-medium text-center text-sm sm:text-base">
                                        {subject.name}
                                    </span>
                                </>
                            );

                            return home ? (
                                <button
                                    key={subject.id}
                                    onClick={() => handleSubjectSelect(subject)}
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </button>
                            ) : (
                                <Link
                                    key={subject.id}
                                    href={route(
                                        type === "course"
                                            ? `${currentLang}.frontend.course.index`
                                            : `${currentLang}.frontend.document.index`,
                                        params,
                                    )}
                                    className="p-4 sm:p-6 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all duration-200 flex flex-col items-center space-y-2 sm:space-y-3"
                                >
                                    {content}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Final Step: Courses and Documents Tabs */}
            {((currentStep >= 2 && isUniversityStage) ||
                (currentStep >= 2 && !isUniversityStage)) && (
                <div className="space-y-4 sm:space-y-6 mt-6">
                    {/* <h3 className="text-base sm:text-lg font-semibold text-center ">
                        {t("click_to_search_results")}
                    </h3> */}
                    <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                        {(includes(currentRouteName.en, "document") ||
                            home) && (
                            <button
                                onClick={() => handleTabClick("documents")}
                                className="w-full sm:flex-1 sm:max-w-sm bg-white border-2 border-teal-600 text-teal-600 px-4 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-teal-600 hover:text-white transition-all duration-200 font-medium text-sm sm:text-base flex items-center justify-center gap-2"
                            >
                                <BookOpen className="size-5 rtl:order-2" />
                                <span className="rtl:order-1">
                                    {t(
                                        "view_all_notes_according_to_search_criteria",
                                    )}
                                </span>
                            </button>
                        )}
                        {(includes(currentRouteName.en, "course") || home) && (
                            <button
                                onClick={() => handleTabClick("courses")}
                                className="w-full sm:flex-1 sm:max-w-sm bg-white border-2 border-teal-600 text-teal-600 px-4 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-teal-600 hover:text-white  transition-all duration-200 font-medium text-sm sm:text-base flex items-center justify-center gap-2"
                            >
                                <GraduationCap className="size-5 rtl:order-2" />
                                <span className="rtl:order-1">
                                    {t(
                                        "view_all_courses_according_to_search_criteria",
                                    )}
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Search Results Component - Only show if home is true and we have search criteria */}
            {home &&
                (selectedStage ||
                    selectedUniversity ||
                    selectedCollege ||
                    selectedGrade ||
                    selectedSubject) && (
                    <SearchResultsComponent
                        searchParams={{
                            stage_id: selectedStage?.id,
                            university_id: selectedUniversity?.id,
                            college_id: selectedCollege?.id,
                            grade_id: selectedGrade?.id,
                            subject_id: selectedSubject?.id,
                        }}
                    />
                )}

            {/* Back Button */}
            {currentStep > 1 && (
                <div className="flex flex-row gap-4 justify-center mt-6 sm:mt-8">
                    <Button
                        variant={"ghost"}
                        onClick={() => handleClickBack()}
                        className="px-4 sm:px-6 py-2 sm:py-3 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200 text-sm sm:text-base font-medium"
                    >
                        {t("back")}
                    </Button>
                    <Button
                        asChild
                        variant={"ghost"}
                        className="px-4 sm:px-6 py-2 sm:py-3 border border-teal-400 text-teal-600 rounded-full hover:bg-teal-700 hover:text-white transition-all duration-200 text-sm sm:text-base font-medium"
                    >
                        <Link
                            href={`${route(current || "home")}`}
                            preserveScroll
                        >
                            {t("reset")}
                        </Link>
                    </Button>
                </div>
            )}
        </div>
    );
}
